import classNames from 'classnames';
import React, { FC, useContext, useMemo } from 'react';
import { t } from 'i18next';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PriceRangeFilter from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/PriceRangeFilter';
import BrokerFilter from './componentes/BrokerFilter';
import MultiFilterModal from 'components/MultiFilterModal';
import FilterIcon from 'icons/Filter.icon';
import { Button } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import { LIMIT_PARAM_KEYWORD } from 'data/global';
import { FilterOption } from 'models/Filters';
import useCreateSelectOptions from 'hooks/useCreateSelectOptions';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { LISTING_STAGE, LISTING_TYPE } from 'models/Listing';
import {
  DEAL_STATUS_FILTER_KEY,
  DEAL_FROM_FILTER_KEY,
  DEAL_TO_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
  LISTING_TYPE_FILTER_KEY,
  VIEW_AS_NAME,
  VIEW_AS_USER,
} from './data';
import { PER_PAGE } from 'components/NumberOfResultsPerPageFilter/NumberOfResultsPerPageFilter.component';
import { DEAL_STATUS_LABEL, LISTING_STATUS_LABEL } from 'data/dealStatus';
import { LOCATION_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { DEAL_STAGE } from 'models/DealRoom';
import { INDUSTRY_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import { ViewAsFilterValue } from './componentes/BrokerFilter/BrokerFilter.component';
import { PriceRangeValue } from 'components/RangeFilterWrapper/RangeFilterWrapper.component';
import useMatchMedia from 'hooks/useMatchMedia';

import './PortfolioSellerFilters.styles.scss';

type PortfolioSellerFiltersProps = {
  className?: string;
};

const PortfolioSellerFilters: FC<PortfolioSellerFiltersProps> = ({
  className,
}) => {
  const [multiFilter, multiFilterRef] = useCallbackRef<ModalRef>();

  const classes = classNames('portfolio-seller-filters', className);
  const {
    isSuperAdmin,
    isEnterpriseHead,
    currentUser: { hasSellingUrgentDeals, isGridViewForDeals },
  } = useContext(CurrentUserContext);

  const createSelectOptions = useCreateSelectOptions();

  const isPhablet = useMatchMedia('isPhablet');

  const { locationOptions, industrySelectOptions } =
    useOperationStepValues(true);

  const isTableView = useMemo(() => !isGridViewForDeals, [isGridViewForDeals]);

  const filterOptions = useMemo(
    () =>
      [
        {
          value: LOCATION_FILTER_KEY,
          label: t('location'),
          isMultiple: true,
          options: locationOptions,
        },
        {
          value: INDUSTRY_FILTER_KEY,
          label: t('industry'),
          isMultiple: true,
          options: industrySelectOptions,
        },
        {
          value: DEAL_TO_FILTER_KEY,
          label: t('ev'),
          render: ({
            value,
            onChange,
          }: {
            value: PriceRangeValue;
            onChange: (value: PriceRangeValue) => void;
          }) => (
            <PriceRangeFilter
              renderAsPortal={!isPhablet}
              title={t('selectAnOption')}
              showIcon={false}
              value={value}
              onApply={onChange}
            />
          ),
          apply: (value: PriceRangeValue) => {
            const from = value?.from;
            const to = value?.to;

            return {
              ...(from && { [DEAL_FROM_FILTER_KEY]: String(from) }),
              ...(to && { [DEAL_TO_FILTER_KEY]: String(to) }),
            };
          },
          getInitialValue: (activeFilter: Record<string, string>) => {
            const from = activeFilter[DEAL_FROM_FILTER_KEY];
            const to = activeFilter[DEAL_TO_FILTER_KEY];

            return {
              ...(from && { from: +from }),
              ...(to && { to: +to }),
            };
          },
        },
        ...(isSuperAdmin || isEnterpriseHead
          ? [
              {
                value: VIEW_AS_USER,
                label: t('viewAs', { name: '' }),
                render: ({
                  value,
                  onChange,
                }: {
                  value: ViewAsFilterValue;
                  onChange: (value: ViewAsFilterValue) => void;
                }) => (
                  <BrokerFilter
                    viewAsUserId={value?.id}
                    viewAsUserName={value?.name}
                    onSelect={onChange}
                  />
                ),
                apply: (value: ViewAsFilterValue) => ({
                  [VIEW_AS_USER]: value.id,
                  [VIEW_AS_NAME]: value.name,
                }),
                getInitialValue: (activeFilter: Record<string, string>) => ({
                  id: activeFilter[VIEW_AS_USER],
                  name: activeFilter[VIEW_AS_NAME] || '',
                }),
              },
            ]
          : []),
        {
          value: DEAL_STATUS_FILTER_KEY,
          label: t('stage'),
          options: Object.values(DEAL_STAGE).map((value) => ({
            value,
            label: t(DEAL_STATUS_LABEL[value]),
          })),
        },
        {
          value: LISTING_STATUS_FILTER_KEY,
          label: t('listing'),
          options: Object.values(LISTING_STAGE).map((value) => ({
            value,
            label: t(LISTING_STATUS_LABEL[value]),
          })),
        },
        ...(hasSellingUrgentDeals
          ? [
              {
                value: LISTING_TYPE_FILTER_KEY,
                label: t('dealType'),
                options: Object.values(LISTING_TYPE).map((value) => ({
                  value,
                  label: t(value),
                })),
              },
            ]
          : []),
        ...(isTableView
          ? [
              {
                value: LIMIT_PARAM_KEYWORD,
                label: t('limit'),
                options: createSelectOptions(PER_PAGE),
              },
            ]
          : []),
      ] as FilterOption[],
    [
      isTableView,
      locationOptions,
      industrySelectOptions,
      hasSellingUrgentDeals,
      isSuperAdmin,
      isEnterpriseHead,
      isPhablet,
      createSelectOptions,
    ],
  );

  return (
    <div className={classes}>
      <Button variant="icon" onClick={multiFilter?.open}>
        <FilterIcon />
      </Button>
      <MultiFilterModal ref={multiFilterRef} filters={filterOptions} />
    </div>
  );
};

export default PortfolioSellerFilters;
